import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import type { PaymentTermRule } from '@ztechapps/common.contract'
import { PaymentTermDueEventEnum, PaymentTermTypeEnum } from '@ztechapps/common.contract'
import { jsonTryParse } from '../json'

/**
 * Parse a stored Template.value (JSON string) into a typed PaymentTermRule.
 *
 * Returns undefined for a missing / blank / unparseable value, an unknown `type`,
 * or a rule missing the numeric fields its type requires. Numeric fields are
 * coerced via toNumber and rejected when NaN, so a malformed payload never yields
 * a half-built rule. The shared shape backs the term editor, the AR aging due-date
 * derivation, and the on-account indicator.
 */
export function parsePaymentTermRule(value?: string): PaymentTermRule | undefined {
  if (isEmpty(value)) {
    return undefined
  }

  const parsed = jsonTryParse(value)
  if (!parsed || typeof parsed !== 'object') {
    return undefined
  }

  switch (parsed.type) {
    case PaymentTermTypeEnum.standard: {
      const dueDays = toNumber(parsed.dueDays)
      if (isNaN(dueDays)) {
        return undefined
      }

      const discountPct = toNumber(parsed.discountPct)
      const discountDays = toNumber(parsed.discountDays)

      return {
        type: PaymentTermTypeEnum.standard,
        dueDays,
        ...(isNaN(discountPct) ? {} : { discountPct }),
        ...(isNaN(discountDays) ? {} : { discountDays }),
      }
    }

    case PaymentTermTypeEnum.downPayment: {
      const downPct = toNumber(parsed.downPct)
      if (isNaN(downPct)) {
        return undefined
      }

      return {
        type: PaymentTermTypeEnum.downPayment,
        downPct,
        dueEvent: PaymentTermDueEventEnum.shipping,
      }
    }

    case PaymentTermTypeEnum.card:
      return { type: PaymentTermTypeEnum.card }

    default:
      return undefined
  }
}
